import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../services/api';
import { theme } from '../../theme';

const POLICY = [
  { id: 'len',   label: '8–12 characters',               test: (p) => p.length >= 8 && p.length <= 12 },
  { id: 'upper', label: 'At least one uppercase letter',  test: (p) => /[A-Z]/.test(p) },
  { id: 'lower', label: 'At least one lowercase letter',  test: (p) => /[a-z]/.test(p) },
  { id: 'digit', label: 'At least one number',            test: (p) => /\d/.test(p) },
  { id: 'spec',  label: 'At least one special character', test: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?`~]/.test(p) },
];

function EyeIcon({ open }) {
  return open ? (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export default function ChangePassword() {
  const navigate = useNavigate();
  const [oldPw, setOldPw]         = useState('');
  const [newPw, setNewPw]         = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showOld, setShowOld]     = useState(false);
  const [showNew, setShowNew]     = useState(false);
  const [showCon, setShowCon]     = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const [loading, setLoading]     = useState(false);

  const checks  = POLICY.map((p) => ({ ...p, ok: p.test(newPw) }));
  const allPass = checks.every((c) => c.ok);
  const matchOk = confirmPw.length > 0 && newPw === confirmPw;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!oldPw) { setError('Please enter your current password.'); return; }
    if (!allPass) { setError('New password does not meet the policy requirements.'); return; }
    if (newPw !== confirmPw) { setError('Passwords do not match.'); return; }
    setError(''); setSuccess('');
    setLoading(true);
    try {
      await auth.changePassword(oldPw, newPw);
      setSuccess('Password changed successfully. Please log in again.');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (extra = {}) => ({
    width: '100%', boxSizing: 'border-box',
    border: `1px solid ${theme.border}`,
    borderRadius: 8, padding: '9px 38px 9px 12px',
    fontSize: 14, background: theme.white, color: theme.text,
    outline: 'none', ...extra,
  });

  return (
    <div style={{ maxWidth: 480, margin: '40px auto', padding: '0 16px' }}>
      <div style={{
        background: theme.white, border: `1px solid ${theme.border}`,
        borderRadius: 12, padding: 32,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: theme.text }}>
          Change Password
        </h2>
        <p style={{ margin: '0 0 24px', fontSize: 13, color: theme.textMuted }}>
          Choose a strong password that you haven't used before.
        </p>

        {error && (
          <div style={{
            background: '#FEF2F2', border: '1px solid #FECACA',
            borderRadius: 8, padding: '10px 14px', color: '#DC2626',
            fontSize: 13, marginBottom: 16,
          }}>{error}</div>
        )}
        {success && (
          <div style={{
            background: '#F0FDF4', border: '1px solid #BBF7D0',
            borderRadius: 8, padding: '10px 14px', color: '#16A34A',
            fontSize: 13, marginBottom: 16,
          }}>{success}</div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Current Password */}
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: theme.text, marginBottom: 6 }}>
            Current Password
          </label>
          <div style={{ position: 'relative', marginBottom: 16 }}>
            <input
              type={showOld ? 'text' : 'password'}
              style={inputStyle()}
              value={oldPw}
              onChange={(e) => setOldPw(e.target.value)}
              placeholder="Enter current password"
              autoComplete="current-password"
            />
            <button type="button" onClick={() => setShowOld(v => !v)}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: theme.textMuted, display: 'flex', padding: 2 }}>
              <EyeIcon open={showOld} />
            </button>
          </div>

          {/* New Password */}
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: theme.text, marginBottom: 6 }}>
            New Password
          </label>
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <input
              type={showNew ? 'text' : 'password'}
              style={inputStyle()}
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder="Enter new password"
              autoComplete="new-password"
            />
            <button type="button" onClick={() => setShowNew(v => !v)}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: theme.textMuted, display: 'flex', padding: 2 }}>
              <EyeIcon open={showNew} />
            </button>
          </div>

          {/* Policy checklist */}
          <div style={{
            background: theme.pageBg, border: `1px solid ${theme.border}`,
            borderRadius: 8, padding: '10px 14px', marginBottom: 16,
          }}>
            <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Password Requirements
            </p>
            {checks.map((c) => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4, fontSize: 12 }}>
                <span style={{ color: c.ok ? '#16A34A' : '#94a3b8', fontSize: 14, lineHeight: 1 }}>
                  {c.ok ? '✓' : '○'}
                </span>
                <span style={{ color: c.ok ? '#16A34A' : theme.textMuted }}>{c.label}</span>
              </div>
            ))}
          </div>

          {/* Confirm Password */}
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: theme.text, marginBottom: 6 }}>
            Confirm New Password
          </label>
          <div style={{ position: 'relative', marginBottom: 4 }}>
            <input
              type={showCon ? 'text' : 'password'}
              style={inputStyle({
                borderColor: confirmPw.length > 0 ? (matchOk ? '#86EFAC' : '#FECACA') : theme.border,
              })}
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              placeholder="Re-enter new password"
              autoComplete="new-password"
            />
            <button type="button" onClick={() => setShowCon(v => !v)}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: theme.textMuted, display: 'flex', padding: 2 }}>
              <EyeIcon open={showCon} />
            </button>
          </div>
          {confirmPw.length > 0 && !matchOk && (
            <p style={{ color: '#DC2626', fontSize: 12, margin: '4px 0 12px' }}>Passwords do not match</p>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                flex: 1, padding: '10px', borderRadius: 8,
                border: `1px solid ${theme.border}`, background: theme.white,
                color: theme.text, fontWeight: 600, fontSize: 14, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 2, padding: '10px', borderRadius: 8, border: 'none',
                background: theme.primary, color: theme.white,
                fontWeight: 700, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Updating…' : 'Update Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
